<template>
  <div class="flow-details__wrap">
    <div class="flow-details__inp-wr">
      <div class="flow-details__inp-name">
        <ui-input
          v-model="name"
          :label="t('Name')"
          :placeholder="t('Name')"
          :error-messages="errorsName"
        />
      </div>
    </div>
    <div>
      <ui-radio-group class="flow-radio-gr mb-8" v-model="pRelation">
        <template #label>
          <div class="font-weight-bold">
            {{ t('Logical relation') }}
            <ui-tooltip-icon
              preset="help"
              class="mb-2 ml-1"
              :text="t('Logical relations between conditions in rule')"
            />
          </div>
        </template>
        <div class="d-flex align-baseline mt-8">
          <ui-radio value="and" :label="t('Logical AND')" />
          <ui-radio class="ml-16" value="or" :label="t('Logical OR')" />
        </div>
      </ui-radio-group>
    </div>
    <div class="action-buttons button-wrapper mb-8">
      <div class="action-buttons--left">
        <ui-button
          icon="fas fa-plus"
          :text="t('Add condition')"
          :has-menu="true"
          button-style="accent"
          :menu-list="menuList"
          :menu-props="{
            maxHeight: 'calc(100vh - 150px)',
            nudgeTop: 150,
            condenced: true,
          }"
        />

        <ui-button
          icon="far fa-trash-alt"
          :text="t('Remove all conditions')"
          @click="deselectAll"
        />
      </div>

      <ui-info
        v-if="isClientHintsNoticeVisible"
        :close="true"
        name="clientHintsNoticeRulesSettings"
        width="w-auto"
      >
        <div>
          <a href="/app/profile">
            {{ t('Activate Client Hints to get more details on OS, device, browser reports.') }}
          </a>
        </div>
      </ui-info>
    </div>

    <div class="flow-rule__card">
      <div v-if="selectedDictionaryEmpty" class="flow-rule__empty">{{ t('Rule conditions are empty') }}</div>
      <brand-and-model
        v-if="selectedDictionary['brand']"
        @close="remove('brand')"
        :conditions="rule.conditions"
      />
      <browser-and-version
        v-if="selectedDictionary['browser']"
        @close="remove('browser')"
        :conditions="rule.conditions"
      />
      <connection-type
        v-if="selectedDictionary['connectType']"
        @close="remove('connectType')"
        :conditions="rule.conditions"
      />
      <dictionary
        v-if="selectedDictionary['country']"
        @close="remove('country')"
        :title="t('Country')"
        :searchPlaceholder="t('Select countries...')"
        api="countries"
        name="country"
        :allow-custom="false"
        :conditions="rule.conditions"
        :withCustomArea="true"
      />
      <dictionary
        v-if="selectedDictionary['state']"
        @close="remove('state')"
        :title="t('State / region')"
        :searchPlaceholder="t('Type state / region name...')"
        api="states"
        name="state"
        :allow-custom="false"
        :conditions="rule.conditions"
        :withCustomArea="true"
      />
      <dictionary
        v-if="selectedDictionary['city']"
        @close="remove('city')"
        :title="t('City')"
        :searchPlaceholder="t('Type city name...')"
        name="city"
        api="cities"
        :allow-custom="false"
        :conditions="rule.conditions"
        :withCustomArea="true"
      />
      <custom-token
        v-if="selectedDictionary['customToken']"
        @close="remove('customToken')"
        :conditions="rule.conditions"
      />
      <external-id
        v-if="selectedDictionary['externalId']"
        @close="remove('externalId')"
        :conditions="rule.conditions"
      />
      <query-header
        v-if="selectedDictionary['queryHeader']"
        @close="remove('queryHeader')"
        :conditions="rule.conditions"
      />
      <custom
        v-for="index in customConditions"
        :key="index"
        @close="remove('custom' + index)"
        :title="t('Custom {0}', [index])"
        :name="'custom' + index"
        :conditions="rule.conditions"
      />
      <day-of-week
        v-if="selectedDictionary['dayOfWeek']"
        @close="remove('dayOfWeek')"
        :conditions="rule.conditions"
      />
      <device-type
        v-if="selectedDictionary['deviceType']"
        @close="remove('deviceType')"
        :conditions="rule.conditions"
      />
      <ip
        v-if="selectedDictionary['ip']"
        @close="remove('ip')"
        :with-custom-area="true"
        :conditions="rule.conditions"
      />
      <dictionary
        v-if="selectedDictionary['isp']"
        @close="remove('isp')"
        :title="t('ISP')"
        :searchPlaceholder="t('Type ISP name...')"
        name="isp"
        api="isps"
        :allow-custom="false"
        :conditions="rule.conditions"
        :withCustomArea="true"
      />
      <dictionary
        v-if="selectedDictionary['language']"
        @close="remove('language')"
        :title="t('Language')"
        :searchPlaceholder="t('Select languages...')"
        api="languages"
        name="language"
        :conditions="rule.conditions"
        :withCustomArea="true"
        :allow-custom="false"
      />
      <dictionary
        v-if="selectedDictionary['mobileCarrier']"
        @close="remove('mobileCarrier')"
        :title="t('Mobile carrier')"
        :searchPlaceholder="t('Type mobile carrier name...')"
        name="mobileCarrier"
        api="mobile-carriers"
        :conditions="rule.conditions"
        :withCustomArea="true"
      />
      <os-and-version
        v-if="selectedDictionary['os']"
        @close="remove('os')"
        :conditions="rule.conditions"
      />
      <referrerDomain
        v-if="selectedDictionary['referrerDomain']"
        @close="remove('referrerDomain')"
        :conditions="rule.conditions"
      />
      <referrer
        v-if="selectedDictionary['referrer']"
        @close="remove('referrer')"
        :conditions="rule.conditions"
      />
      <time-of-day
        v-if="selectedDictionary['timeOfDay']"
        @close="remove('timeOfDay')"
        :conditions="rule.conditions"
      />
      <timezone
        v-if="isTimezoneAvailable"
        :conditions="rule.conditions"
      />
      <user-agent
        v-if="selectedDictionary['userAgent']"
        @close="remove('userAgent')"
        :with-custom-area="true"
        :conditions="rule.conditions"
      />
      <webview
        v-if="selectedDictionary['webViewMark']"
        @close="remove('webViewMark')"
        :conditions="rule.conditions"
      />
      <crawler
        v-if="selectedDictionary['crawler']"
        @close="remove('crawler')"
        :conditions="rule.conditions"
      />
      <proxy
        v-if="selectedDictionary['proxy']"
        @close="remove('proxy')"
        :conditions="rule.conditions"
      />
      <ipBlacklisted
        v-if="selectedDictionary['ipBlacklisted']"
        @close="remove('ipBlacklisted')"
        :conditions="rule.conditions"
      />
      <ipDataCenter
        v-if="selectedDictionary['ipDataCenter']"
        @close="remove('ipDataCenter')"
        :conditions="rule.conditions"
      />
      <ipWithFrequentEvents
        v-if="selectedDictionary['ipWithFrequentEvents']"
        @close="remove('ipWithFrequentEvents')"
        :conditions="rule.conditions"
      />
      <ipWithFrequentCampaignEvents
        v-if="selectedDictionary['ipWithFrequentCampaignEvents']"
        @close="remove('ipWithFrequentCampaignEvents')"
        :conditions="rule.conditions"
      />
      <visitCountryMatch
        v-if="selectedDictionary['visitCountryMatch']"
        @close="remove('visitCountryMatch')"
        :conditions="rule.conditions"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import BrandAndModel from '@/views/reports/forms/flow/views/conditions/brand-and-model.vue';
import BrowserAndVersion from '@/views/reports/forms/flow/views/conditions/browser-and-version.vue';
import OsAndVersion from '@/views/reports/forms/flow/views/conditions/os-and-version.vue';
import Dictionary from '@/views/reports/forms/flow/views/conditions/dictionary.vue';
import ConnectionType from '@/views/reports/forms/flow/views/conditions/connection-type.vue';
import Custom from '@/views/reports/forms/flow/views/conditions/custom.vue';
import CustomToken from '@/views/reports/forms/flow/views/conditions/custom-token.vue';
import Crawler from '@/views/reports/forms/flow/views/conditions/crawler.vue';
import QueryHeader from '@/views/reports/forms/flow/views/conditions/query-header.vue';
import DayOfWeek from '@/views/reports/forms/flow/views/conditions/day-of-week.vue';
import TimeOfDay from '@/views/reports/forms/flow/views/conditions/time-of-day.vue';
import Timezone from '@/views/reports/forms/flow/views/conditions/timezone.vue';
import DeviceType from '@/views/reports/forms/flow/views/conditions/device-type.vue';
import Ip from '@/views/reports/forms/flow/views/conditions/ip.vue';
import Referrer from '@/views/reports/forms/flow/views/conditions/referrer.vue';
import ReferrerDomain from '@/views/reports/forms/flow/views/conditions/referrer-domain.vue';
import UserAgent from '@/views/reports/forms/flow/views/conditions/user-agent.vue';
import Webview from '@/views/reports/forms/flow/views/conditions/webview.vue';
import Proxy from '@/views/reports/forms/flow/views/conditions/proxy.vue';
import VisitCountryMatch from '@/views/reports/forms/flow/views/conditions/visit-country-match.vue';
import IpBlacklisted from '@/views/reports/forms/flow/views/conditions/ip-blacklisted.vue';
import IpDataCenter from '@/views/reports/forms/flow/views/conditions/ip-data-center.vue';
import IpWithFrequentEvents from '@/views/reports/forms/flow/views/conditions/ip-with-frequent-events.vue';
import IpWithFrequentCampaignEvents from '@/views/reports/forms/flow/views/conditions/ip-with-frequent-campaign-events.vue';
import ExternalId from '@/views/reports/forms/flow/views/conditions/external-id.vue';
import { useBotRuleSettings } from './useBotRuleSettings';

const props = defineProps<{
  rule: {
    conditions: Record<string, unknown>;
  };
  errorsName?: string[];
  active_name: string;
  relation?: string;
}>();

const emit = defineEmits<{
  'update:active_name': [value: string];
  'update:relation': [value: string];
}>();

const name = computed({
  get: () => props.active_name,
  set: (value) => emit('update:active_name', value),
});

const pRelation = computed({
  get: () => props.relation || 'and',
  set: (value) => emit('update:relation', value),
});

const {
  t,
  conditions,
  menuList,
  selectedDictionary,
  selectedDictionaryEmpty,
  customConditions,
  isTimezoneAvailable,
  isClientHintsNoticeVisible,
  remove,
  deselectAll,
  resetValidation,
} = useBotRuleSettings(props);

defineExpose({
  conditions,
  resetValidation,
});
</script>

<style lang="scss" scoped>
.button-wrapper {
  display: flex;
}

.flow-rule__card {
  overflow-y: auto;
  box-shadow: 0 0 3px inset;
  padding: 12px;
}

.flow-rule__empty {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80px;
  width: 100%;
  border: 2px dashed var(--color-gray10);
}

.action-buttons{
  display: flex;
  justify-content:space-between;
  flex-wrap: wrap;
  gap:6px;
}

.action-buttons--left{
  display: flex;
  flex-wrap: nowrap;
}
</style>
